// ═══════════════════════════════════════════════════════════════
//  parcUtils.js — Helpers du module Parc Machines (v8)
// ═══════════════════════════════════════════════════════════════
import { storage } from "../../firebase";
import { EPJ } from "../../core/theme";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getRoles } from "../../core/permissions";

// ─── Statut d'un outil ──────────────────────────────────────
export const OUTIL_STATUTS = {
  disponible:    { label: "Disponible",    color: EPJ.green, icon: "✓" },
  sorti:         { label: "Sorti",         color: EPJ.orange, icon: "→" },
  en_retard:     { label: "En retard",     color: EPJ.red, icon: "⏰" },
  maintenance:   { label: "Maintenance",   color: EPJ.catEtude, icon: "🛠" },
  hors_service:  { label: "Hors service",  color: EPJ.gray500, icon: "✕" },
  affecte:       { label: "Attribué",      color: EPJ.blue, icon: "👤" },
};

// ─── Date utils ─────────────────────────────────────────────
export const todayISO = () => new Date().toISOString().split("T")[0];

export function isLate(dateRetour, dateRetourReelle) {
  if (dateRetourReelle) return false;
  if (!dateRetour) return false;
  const d = new Date(dateRetour + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR");
}

export function formatDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

// ─── Statut effectif d'un outil ──────────────────────────────
export function computeOutilStatut(outil, sorties) {
  if (outil.statut === "hors_service") return "hors_service";
  if (outil.statut === "maintenance") return "maintenance";
  if (outil.affectationPermanenteUserId) return "affecte";
  const sortieEnCours = sorties.find(
    s => s.outilId === outil._id && !s.dateRetourReelle
  );
  if (!sortieEnCours) return "disponible";
  if (isLate(sortieEnCours.dateRetourPrevue)) return "en_retard";
  return "sorti";
}

export function findSortieEnCours(outilId, sorties) {
  return sorties.find(s => s.outilId === outilId && !s.dateRetourReelle) || null;
}

// ─── Catégories (dynamiques depuis Firestore) ───────────────
export function getCategorieById(categoriesArr, catId) {
  return categoriesArr.find(c => c.id === catId || c._id === catId) || null;
}

export function getCategorieLabel(categoriesArr, catId) {
  const c = getCategorieById(categoriesArr, catId);
  return c?.label || catId || "—";
}

export function getCategorieIcon(categoriesArr, catId) {
  const c = getCategorieById(categoriesArr, catId);
  return c?.icon || "🔧";
}

// ─── Droit "autorisé à sortir un outil" ─────────────────────
// Admin et Direction : toujours true par défaut
// Autres rôles : false par défaut, doit être activé via `canSortirOutil: true` sur le user
export function canSortirOutil(user) {
  if (!user) return false;
  if (user.canSortirOutil === true) return true;
  if (user.canSortirOutil === false) return false;
  // Utilise getRoles() pour gérer les 3 formats possibles (roles / role / fonction)
  const roles = getRoles(user).map(r => (r || "").toLowerCase());
  return roles.includes("admin") || roles.includes("direction") || roles.includes("achat");
}

// 🔴 MIROIR des règles Firestore du parc — LE prédicat des boutons
// d'écriture du parc. Il reflète `match /outils` :
//     allow create, update: if isAssistante() || isAchat() || estResponsableParc()
// soit Admin, Direction, Conducteur travaux, Assistante, Achat (isAssistante()
// est cumulatif dans firestore.rules), plus le responsable parc.
//
// Le responsable parc gère le parc au quotidien — sorties, retours, pannes,
// catalogue — sans être Admin, Direction ni Achat. Le flag vient de la FICHE
// `utilisateurs` côté écran, et du CLAIM `responsableParc` côté règles : les
// deux doivent dire la même chose, sinon un bouton s'affiche pour rien.
//
// ⚠️ Le claim n'arrive dans le jeton qu'après un passage de la callable
// adminReposerClaims ET une reconnexion. Entre les deux, l'écran (qui lit la
// fiche) est en avance sur la règle (qui lit le jeton) : le bouton s'affiche
// et Firestore refuse. C'est transitoire, et c'est pour ça que la reprise
// des jetons doit précéder la mise en ligne de cet écran.
//
// 🔴 NE PAS en créer un second. Si un écran a besoin d'un périmètre
// différent, c'est que la RÈGLE est différente : nommer le nouveau prédicat
// d'après la règle qu'il reflète, jamais d'après l'écran qui l'appelle.
const ROLES_ECRITURE_PARC = ["Admin", "Direction", "Conducteur travaux", "Assistante", "Achat"];
export function peutGererParc(user) {
  if (!user) return false;
  if (user.responsableParc === true) return true;
  return getRoles(user).some(r => ROLES_ECRITURE_PARC.includes(r));
}

// 🔴 MIROIR de `outillageCategories` et `outillagePannes` :
//     allow write: if isAdminAchatOuResponsableParc()
// soit Admin, Achat, responsable parc — et PERSONNE D'AUTRE. Ces deux
// référentiels sont plus fermés que `outils` : ni la Direction, ni
// l'Assistante, ni le Conducteur n'y ont droit.
//
// C'est un SECOND prédicat, et c'est assumé : le périmètre diffère parce
// que la RÈGLE diffère. Il est nommé d'après la règle qu'il reflète, pas
// d'après l'écran qui l'appelle — la seule façon de ne pas en accumuler un
// par écran. Utiliser peutGererParc() ici ouvrirait à la Direction deux
// écrans que Firestore lui refuse, en silence, au moment d'enregistrer.
export function peutGererReferentielsParc(user) {
  if (!user) return false;
  if (user.responsableParc === true) return true;
  return getRoles(user).some(r => r === "Admin" || r === "Achat");
}

// Droit de gérer le catalogue (outils + catégories + pannes)
// ⚠️ NE PLUS UTILISER POUR LE PARC : remplacé par peutGererParc(), qui est
// le miroir des règles. Ce prédicat ne survit que pour AdminSmsTemplates,
// qui n'a rien à voir avec le parc et s'en sert comme garde d'accès. Lui
// ajouter le flag `responsableParc` ouvrirait les modèles SMS au
// responsable parc — un élargissement hors périmètre, déguisé en cohérence.
export function canGererCatalogue(user) {
  if (!user) return false;
  const roles = getRoles(user).map(r => (r || "").toLowerCase());
  return roles.includes("admin")
    || roles.includes("direction")
    || roles.includes("assistante")
    || roles.includes("achat");
}

// v10.M — Droits spécifiques à l'import/export Excel du parc d'outils.
// Plus restrictif que canGererCatalogue : on retire Assistante (qui gère
// le catalogue 1-à-1 mais ne touche pas au bulk), on ajoute le
// Responsable parc machines (flag user.responsableParc).
// Q5 validé par PJY : Admin + Direction + Responsable parc.
export function canImportExportOutils(user) {
  if (!user) return false;
  const roles = getRoles(user).map(r => (r || "").toLowerCase());
  if (roles.includes("admin")) return true;
  if (roles.includes("direction")) return true;
  if (roles.includes("achat")) return true;
  if (user.fonction === "Admin" || user.fonction === "Direction") return true;
  if (user.responsableParc === true) return true;
  return false;
}

// ─── Compression + upload photo Firebase Storage ────────────
export async function compressImage(file, maxWidth = 1024, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = () => reject(new Error("Lecture fichier échouée"));
    reader.readAsDataURL(file);

    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error("Compression échouée")),
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => reject(new Error("Image invalide"));
  });
}

export async function uploadOutilPhoto(outilId, file, onProgress) {
  if (onProgress) onProgress("Compression…");
  const blob = await compressImage(file, 1024, 0.85);
  if (onProgress) onProgress("Téléversement…");
  const path = `outils/${outilId}_${Date.now()}.jpg`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, blob, { contentType: "image/jpeg" });
  if (onProgress) onProgress("Finalisation…");
  const url = await getDownloadURL(fileRef);
  return { url, path };
}

// ─── Upload générique vers un dossier quelconque de Storage ────
// folder : ex "articles", "commandes", "reserves" …
// itemId : identifiant utilisé comme préfixe du fichier
export async function uploadPhotoToFolder(folder, itemId, file, onProgress) {
  if (onProgress) onProgress("Compression…");
  const blob = await compressImage(file, 1024, 0.85);
  if (onProgress) onProgress("Téléversement…");
  const safeId = String(itemId || "item").replace(/[\/\s]/g, "_");
  const path = `${folder}/${safeId}_${Date.now()}.jpg`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, blob, { contentType: "image/jpeg" });
  if (onProgress) onProgress("Finalisation…");
  const url = await getDownloadURL(fileRef);
  return { url, path };
}

export async function deletePhotoByPath(path) {
  if (!path) return;
  try {
    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
  } catch (e) {
    console.warn("deletePhotoByPath (non bloquant):", e.message);
  }
}

export async function deleteOutilPhoto(path) {
  if (!path) return;
  try {
    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
  } catch (e) {
    console.warn("deleteOutilPhoto (non bloquant):", e.message);
  }
}

// ─── Génération d'ID simple (timestamp + random) ────────────
export function generateId(prefix = "o") {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
}

// ─── Rendu d'un template SMS avec variables ─────────────────
// Remplace {prenom}, {ref}, {nom}, {dateRetour}, {chantier} par les valeurs
export function renderSmsTemplate(template, vars) {
  if (!template) return "";
  let out = template;
  Object.entries(vars || {}).forEach(([k, v]) => {
    const re = new RegExp(`\\{${k}\\}`, "g");
    out = out.replace(re, v ?? "");
  });
  return out;
}

// Copie dans le presse-papier (avec fallback pour iOS ancien)
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    console.error("copyToClipboard:", e);
    return false;
  }
}
